import * as XLSX from "xlsx-js-style";
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { localizeAttendanceTime } from '../../../Utils/indonesiaTimezone';

export const exportAttendanceDetail = (attendanceData, startDate, endDate, selectedStatus) => {
    const headers = [
        "Employee ID",
        "Full Name",
        "Branch",
        "Organization",
        "Job Position",
        "Date",
        "Shift",
        "Shift Code",
        "Shift Label",
        "Schedule Check In",
        "Schedule Check Out",
        "Attendance Code",
        "Time Off Code",
        "Hourly Time Off Breakdown",
        "Check In",
        "Check In Time Zone",
        "Check Out",
        "Check Out Time Zone",
        "Schedule Break Start",
        "Schedule Break End",
        "Actual Break Start",
        "Actual Break End",
        "Late In",
        "Early Out",
        "Schedule Working Hour",
        "Actual Working Hour",
        "Real Working Hour",
        "Overtime Duration Before",
        "Overtime Duration After",
        "Hourly Time Off Taken",
        "Cordinate Clock In",
        "Cordinate Clock Out",
        "Location Clock In",
        "Location Clock Out"
    ];

    const data = [];

    attendanceData?.forEach((user) => {
        // API attendance saat ini mengirim jam dalam WIB. Lokalkan setiap
        // record berdasarkan latitude/longitude sebelum dipakai di export.
        const localizedAttendance = (user.attendance || []).map((attendance) => {
            const localized = localizeAttendanceTime(
                attendance.date,
                attendance.time,
                attendance.latitude,
                attendance.longitude
            );

            return {
                ...attendance,
                _localDate: localized.date || dayjs(attendance.date).format('YYYY-MM-DD'),
                _localTime: localized.time || attendance.time || '',
                _timeZone: localized.zone,
            };
        });

        // Hitung total durasi menggunakan jam lokal hasil konversi.
        let totalDuration = 0;
        localizedAttendance.forEach((a) => {
            if (a.status !== "checkin") return;

            const originalDate = dayjs(a.date).format("YYYY-MM-DD");
            const checkout = localizedAttendance.find(
                (b) => dayjs(b.date).format("YYYY-MM-DD") === originalDate && b.status === "checkout"
            );

            if (a._localTime && checkout?._localTime) {
                const checkIn = dayjs(`${a._localDate} ${a._localTime}`);
                const checkOut = dayjs(`${checkout._localDate} ${checkout._localTime}`);
                if (checkOut.isAfter(checkIn)) {
                    totalDuration += checkOut.diff(checkIn, "minute");
                }
            }
        });

        // Format durasi total ke "X Jam Y Menit"
        const formatDuration = (minutes) => {
            const jam = Math.floor(minutes / 60);
            const menit = minutes % 60;
            return `${jam} Jam ${menit} Menit`;
        };

        // Separator row
        const separatorRow = Array(headers.length).fill("");
        separatorRow[0] = `${user.users_code} - ${user.name} - ${formatDuration(totalDuration)}`;
        data.push(separatorRow);


        // Mapping absensi memakai tanggal lokal. Jika konversi WITA/WIT
        // melewati tengah malam, record otomatis masuk ke tanggal lokalnya.
        const attendancesByDate = {};
        localizedAttendance.forEach((a) => {
            const date = a._localDate;
            if (!attendancesByDate[date]) attendancesByDate[date] = {};
            attendancesByDate[date][a.status] = a;
        });

        // Mapping user_shift per tanggal
        const userShiftMap = {};
        user.user_shift?.forEach((s) => {
            const date = dayjs(s.date).format('YYYY-MM-DD');
            userShiftMap[date] = s.shift;
        });

        // Mapping shift berdasarkan nama hari schedule.shift_day
        const shiftDayMap = {};
        user.schedule?.shift_day?.forEach((s) => {
            shiftDayMap[s.day_name?.toLowerCase()] = s.shift;
        });

        // Mapping leave per tanggal
        const leaveMap = {};
        user.leave_paid?.forEach((l) => {
            if (l.leave_type != null) {
                const start = dayjs(l.date_start);
                const end = dayjs(l.date_end);
                for (let d = start; d.isBefore(end) || d.isSame(end, 'day'); d = d.add(1, 'day')) {
                    leaveMap[d.format('YYYY-MM-DD')] = l.leave_type.name;
                }
            }
        });

        const getTime = (val) => val ? dayjs(`${val}`).format('HH:mm') : '00:00';
        const formatMinute = (min) =>
            `${Math.floor(min / 60).toString().padStart(2, '0')}:${(min % 60).toString().padStart(2, '0')}`;

        const start = dayjs(startDate);
        const end = dayjs(endDate);

        for (let d = start; d.isBefore(end) || d.isSame(end, 'day'); d = d.add(1, 'day')) {
            const date = d.format('YYYY-MM-DD');
            const att = attendancesByDate[date] || {};

            const dayName = d.format('dddd').toLowerCase();
            const shift = userShiftMap[date] || shiftDayMap[dayName];

            const timeIn = shift?.time_in || '00:00';
            const timeOut = shift?.time_out || '00:00';
            const breakStart = shift?.break_start || '00:00';
            const breakEnd = shift?.break_end || '00:00';

            const checkInTime = att?.checkin?._localTime || '';
            const checkOutTime = att?.checkout?._localTime || '';
            const checkInTimeZone = att?.checkin?._timeZone || 'WIB';
            const checkOutTimeZone = att?.checkout?._timeZone || 'WIB';

            const scheduleIn = dayjs(`${date} ${timeIn}`);
            const scheduleOut = dayjs(`${date} ${timeOut}`);
            const checkIn = checkInTime ? dayjs(`${date} ${checkInTime}`) : null;
            const checkOut = checkOutTime ? dayjs(`${date} ${checkOutTime}`) : null;

            const lateIn = checkIn && checkIn.isAfter(scheduleIn)
                ? checkIn.diff(scheduleIn, 'minute')
                : 0;

            const earlyOut = checkOut && checkOut.isBefore(scheduleOut)
                ? scheduleOut.diff(checkOut, 'minute')
                : 0;

            const scheduleDuration = scheduleOut.diff(scheduleIn, 'minute');
            const actualDuration = checkIn && checkOut
                ? checkOut.diff(checkIn, 'minute')
                : 0;

            const realWorkingHour = actualDuration > 0 ? actualDuration : 0;
            const checkInAttendance = attendancesByDate[date]?.checkin;
            const checkOutAttendance = attendancesByDate[date]?.checkout;

            const hasCoordinate = (attendance) => attendance?.latitude !== null && attendance?.latitude !== undefined && attendance?.latitude !== ''
                && attendance?.longitude !== null && attendance?.longitude !== undefined && attendance?.longitude !== '';

            const checkInCordinate = hasCoordinate(checkInAttendance)
                ? `${checkInAttendance.latitude},${checkInAttendance.longitude}`
                : '';

            const checkOutCordinate = hasCoordinate(checkOutAttendance)
                ? `${checkOutAttendance.latitude},${checkOutAttendance.longitude}`
                : '';

            const locationClockIn = checkInCordinate
                ? `https://www.google.com/maps?q=${checkInCordinate}`
                : '';

            const locationClockOut = checkOutCordinate
                ? `https://www.google.com/maps?q=${checkOutCordinate}`
                : '';
            const row = [
                user.users_code,
                user.name,
                user.area?.name || '',
                user.division?.name || '',
                user.position?.name || '',
                date,
                shift?.name || '',
                shift?.shift_id || '',
                shift?.name || '',
                getTime(`${date} ${timeIn}`),
                getTime(`${date} ${timeOut}`),
                '',
                leaveMap[date] || '',
                '',
                getTime(checkInTime ? `${date} ${checkInTime}` : ''),
                checkInTimeZone,
                getTime(checkOutTime ? `${date} ${checkOutTime}` : ''),
                checkOutTimeZone,
                getTime(`${date} ${breakStart}`),
                getTime(`${date} ${breakEnd}`),
                '00:00',
                '00:00',
                formatMinute(lateIn),
                formatMinute(earlyOut),
                formatMinute(scheduleDuration),
                formatMinute(actualDuration),
                formatMinute(realWorkingHour),
                shift?.overtime_before || '00:00',
                shift?.overtime_after || '00:00',
                '00:00',
                checkInCordinate,
                checkOutCordinate,
                locationClockIn,
                locationClockOut
            ];

            if (selectedStatus && !selectedStatus.includes('all')) {
                const isPresent = checkInTime && checkOutTime;
                const isLate = lateIn > 0;
                const isNoClockOut = checkInTime && !checkOutTime;
                const isAlpha = !checkInTime && !checkOutTime;

                const include = (selectedStatus.includes('present') && isPresent) ||
                    (selectedStatus.includes('late') && isLate) ||
                    (selectedStatus.includes('no_clock_out') && isNoClockOut) ||
                    (selectedStatus.includes('alpha') && isAlpha);
                if (!include) continue; // skip hari ini
            }
            data.push(row);
        }
    });

    // Judul dan tanggal
    const title = ['Laporan Detail Kehadiran Karyawan'];
    const dateRange = [`${startDate} s/d ${endDate}`];
    const worksheetData = [title, dateRange, [], headers, ...data];

    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Merge judul dan tanggal
    ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];

    // Styling judul
    ws['A1'].s = {
        font: { bold: true, sz: 20 },
        alignment: { horizontal: 'center' }
    };
    ws['A2'].s = {
        font: { bold: true },
        alignment: { horizontal: 'center' }
    };

    // Styling header (baris ke-4 = index 3)
    const headerRowIndex = 3;
    for (let C = 0; C < headers.length; ++C) {
        const cellAddr = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
        if (!ws[cellAddr]) ws[cellAddr] = { t: 's', v: headers[C] };
        ws[cellAddr].s = {
            fill: {
                fgColor: { rgb: "BDD7EE" }
            },
            font: { bold: true },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
                top: { style: "thin", color: { rgb: "000000" } },
                bottom: { style: "thin", color: { rgb: "000000" } },
                left: { style: "thin", color: { rgb: "000000" } },
                right: { style: "thin", color: { rgb: "000000" } },
            }
        };
    }


    // Cari dan styling baris pemisah user (kolom A yang tidak kosong tapi kolom lain kosong)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
        let isSeparatorRow = false;
        const firstCell = XLSX.utils.encode_cell({ r: R, c: 0 });
        if (ws[firstCell] && ws[firstCell].v && ws[firstCell].v.toString().includes(' - ')) {
            // Pastikan kolom lain kosong semua
            isSeparatorRow = true;
            for (let C = 1; C < headers.length; ++C) {
                const cell = XLSX.utils.encode_cell({ r: R, c: C });
                if (ws[cell] && ws[cell].v) {
                    isSeparatorRow = false;
                    break;
                }
            }
        }
        if (isSeparatorRow) {
            for (let C = 0; C < headers.length; ++C) {
                const cellAddr = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddr]) ws[cellAddr] = { t: 's', v: '' };
                if (!ws[cellAddr].s) ws[cellAddr].s = {};
                ws[cellAddr].s.fill = {
                    fgColor: { rgb: "D9E1F2" } // Warna biru muda, bisa diganti
                };
                ws[cellAddr].s.font = { bold: true };
            }
        }
    }

    // Tambah border semua sel
    for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
            if (!ws[cellAddress]) continue;
            if (!ws[cellAddress].s) ws[cellAddress].s = {};
            // Jangan override fill di baris pemisah user
            if (!ws[cellAddress].s.fill) {
                ws[cellAddress].s.border = {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } },
                };
            }
        }
    }

    // Auto width berdasarkan panjang data terpanjang per kolom
    ws['!cols'] = headers.map((_, i) => {
        const colData = worksheetData.map(row => row[i] ? row[i].toString() : '');
        const maxLen = Math.max(...colData.map(val => val.length), headers[i].length);
        return { wch: maxLen + 2 };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Detail');

    const filename = `Attendance_Detail_${startDate}_to_${endDate}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
};